import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import React from "react";

import styles from "./TaskSection.module.css";

interface TaskSectionProps {
  title: string;
  children: React.ReactNode;
  /**
   * Whether the owning task has completed. A section is rendered from the moment
   * its task starts, so this drives only the header's status icon: a green check
   * when done, a pulsing in-progress dot while the task is still running. Mirrors
   * the task panel's iconography (see `TaskPanel`'s `statusIcon`).
   */
  done?: boolean;
  /**
   * The id of the task this section represents. Surfaced as `data-task-id` on
   * the section root so the task panel's scroll-spy and click-to-jump can find
   * the section even while it is collapsed (its body, and the message articles
   * inside, are unmounted until expanded).
   */
  taskId: number;
  /**
   * Force the section open because a scroll/navigation target lands inside it.
   * The body is unmounted while collapsed, so a "scroll to message N" consumer
   * cannot find a target hidden in a closed section; opening it here keeps the
   * target node in the DOM. Rendered open synchronously (rather than via an
   * effect) so the node exists by the time the scroll query runs. The forced
   * state is latched into the section's own expanded state the moment it
   * arrives, so once the target clears the section stays open *and* the user
   * can collapse it — a jump must not leave the section stuck open.
   */
  expandedTarget?: boolean;
}

/**
 * A lightweight, unobtrusive collapsible that wraps the conversation turns a
 * task spans. Created the moment its task starts (not when it completes) and
 * expanded by default so the work reads inline, with the header tracking the
 * task's live status; the task's intent and resolution live in the task panel,
 * not here. Collapse a section to tuck a long run of tool activity behind its
 * title.
 */
export function TaskSection({
  title,
  children,
  expandedTarget = false,
  done = false,
  taskId,
}: TaskSectionProps) {
  const [userExpanded, setUserExpanded] = React.useState(true);
  // Latch a forced-open target into the user-controlled state so it survives
  // the target clearing (the section stays open) yet remains collapsible (the
  // chevron toggles `userExpanded`, which is no longer pinned true by the
  // never-released force). A layout effect runs before paint and, because child
  // effects run before the parent's scroll effect, the body is already mounted
  // when the parent queries for the scroll anchor.
  React.useLayoutEffect(() => {
    if (expandedTarget) {
      setUserExpanded(true);
    }
  }, [expandedTarget]);
  const expanded = userExpanded || expandedTarget;

  return (
    <div className={styles.section} data-task-id={taskId}>
      <button
        type="button"
        className={styles.header}
        aria-expanded={expanded}
        onClick={() => setUserExpanded((prev) => !prev)}
      >
        {expanded ? (
          <ExpandMoreIcon className={styles.chevron} />
        ) : (
          <ChevronRightIcon className={styles.chevron} />
        )}
        {done ? (
          <CheckCircleIcon className={styles.doneIcon} />
        ) : (
          <RadioButtonCheckedIcon className={styles.activeIcon} />
        )}
        <span className={styles.title}>{title}</span>
      </button>
      {expanded && <div className={styles.body}>{children}</div>}
    </div>
  );
}
